/**
 * HRAttendanceToday — TASK 24c. ภาพรวมการเข้างานวันนี้สำหรับ HR/Manager/Owner.
 *
 * 4 stat cards (ในกะ / สแกนแล้ว / ลา / ยังไม่สแกน), by-shift breakdown,
 * แล้ว 3 tabs (ยังไม่สแกน / สแกนแล้ว / ลา). ในหมวด "ยังไม่สแกน" ทุก row
 * มีปุ่มแจ้งเตือนผ่าน LINE — กดได้ครั้งละคน หรือ bulk ทั้งหมดในคลิกเดียว
 * (throttle 15 นาที + quiet hours guard อยู่ฝั่ง backend).
 */
import { useState } from 'react';
import { AlertTriangle, MapPin } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useAttendanceToday, useNudge, useOrgDepartments, useEmployees, useBranches, type UnscannedRow, type ScannedRow, type OnLeaveRow } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { resolveFileUrl, apiDownload } from '../../lib/api-client';
import { todayISO } from '../../lib/thai-date';

type Tab = 'unscanned' | 'scanned' | 'on_leave';

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'เพิ่งกด';
  if (m < 60) return `${m} นาทีที่แล้ว`;
  const h = Math.floor(m / 60);
  return `${h} ชม.ที่แล้ว`;
}

function todayBkk(): string {
  return todayISO();
}

function currentBkkMonth(): string {
  return todayBkk().slice(0, 7);
}

/** Content-only version used by the Time hub. */
export function HRAttendanceTodayContent() {
  const [date, setDate] = useState(todayBkk);
  const [tab, setTab] = useState<Tab>('unscanned');
  const [deptFilter, setDeptFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const { data, isLoading, refetch } = useAttendanceToday(date);
  const { data: departments } = useOrgDepartments();
  const { data: branches } = useBranches();
  // สาขาไม่ได้มาจาก endpoint นี้ตรงๆ — join ฝั่ง client จาก emp_code → branch_id
  // ของ /hr/employees (ไม่แก้ backend response ของหน้านี้).
  const { data: employees } = useEmployees() as { data: Array<{ emp_code: string; branch_id?: string | null }> | undefined };
  const empBranch = new Map((employees ?? []).map((e) => [e.emp_code, e.branch_id ?? '']));
  const branchNameById = new Map((branches ?? []).map((b) => [b.id, b.name]));
  const branchNameByEmp = (empCode: string): string | null => {
    const bid = empBranch.get(empCode);
    return bid ? branchNameById.get(bid) ?? null : null;
  };
  const nudge = useNudge();
  const toast = useToast();
  const confirm = useConfirm();

  const [exportMonth, setExportMonth] = useState(currentBkkMonth);
  const [exportBranch, setExportBranch] = useState('');
  const [exporting, setExporting] = useState(false);
  async function handleExportAttendance() {
    setExporting(true);
    try {
      const params = new URLSearchParams({ month: exportMonth });
      if (exportBranch) params.set('branch_id', exportBranch);
      await apiDownload(`/hr/attendance/export?${params.toString()}`, `attendance-${exportMonth}.csv`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดรายงานไม่สำเร็จ');
    } finally {
      setExporting(false);
    }
  }

  function matchDept(row: { department: string | null }): boolean {
    return !deptFilter || row.department === deptFilter;
  }
  function matchBranch(row: { emp_code: string }): boolean {
    return !branchFilter || empBranch.get(row.emp_code) === branchFilter;
  }
  // แถวที่สแกนแล้ว: กรองด้วย "สาขาที่เช็คอินจริง" (fallback สาขาหลักถ้าไม่ทราบ) —
  // พนักงานไปช่วยงานต่างสาขาต้องโผล่ใต้สาขาที่ไปเช็คอิน ไม่ใช่สาขาหลัก
  function matchBranchScanned(row: ScannedRow): boolean {
    if (!branchFilter) return true;
    const effective = row.checkin_branch_id ?? empBranch.get(row.emp_code);
    return effective === branchFilter;
  }
  const unscannedList = (data?.unscanned_list ?? []).filter((r) => matchDept(r) && matchBranch(r));
  const scannedList = (data?.scanned_list ?? []).filter((r) => matchDept(r) && matchBranchScanned(r));
  const onLeaveList = (data?.on_leave_list ?? []).filter((r) => matchDept(r) && matchBranch(r));

  async function handleNudge(empCodes: string[]) {
    if (empCodes.length === 0) return;
    const label = empCodes.length === 1 ? `แจ้งเตือน ${empCodes[0]}?` : `แจ้งเตือน ${empCodes.length} คนพร้อมกัน?`;
    const ok = await confirm({
      title: label,
      message: 'จะส่ง Flex message ผ่าน LINE ให้พนักงานเปิด LIFF ไปสแกน',
      confirmText: 'ส่งเลย',
    });
    if (!ok) return;
    try {
      const res = await nudge.mutateAsync({ emp_codes: empCodes, date });
      if (res.sent.length > 0) toast.success(`ส่งแล้ว ${res.sent.length} คน`);
      if (res.throttled.length > 0) {
        const reasons = new Set(res.throttled.map((t) => t.reason));
        if (reasons.has('quiet_hours')) toast.error('อยู่ในช่วงห้ามรบกวน (quiet hours)');
        else if (reasons.has('throttle')) toast.success(`ข้าม ${res.throttled.length} คน (เคยเตือนใน 15 นาทีล่าสุด)`);
        else toast.error(`ข้าม ${res.throttled.length} คน (ไม่มี LINE id)`);
      }
      if (res.failed.length > 0) toast.error(`พลาด ${res.failed.length} คน`);
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'แจ้งเตือนไม่สำเร็จ');
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          max={todayBkk()}
          className="input text-sm"
        />
        <button onClick={() => refetch()} className="btn-secondary text-sm">
          รีเฟรช
        </button>
      </div>

      {/* 4 stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
        <div className="card p-3">
          <p className="text-xs text-gray-500">ในกะวันนี้</p>
          <p className="text-2xl font-bold">{data?.summary.total_in_shift ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">สแกนแล้ว</p>
          <p className="text-2xl font-bold text-emerald-700">{data?.summary.scanned_morning_in ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">ลา</p>
          <p className="text-2xl font-bold text-blue-700">{data?.summary.on_leave ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">ยังไม่สแกน</p>
          <p className="text-2xl font-bold text-amber-700">{data?.summary.unscanned ?? '-'}</p>
        </div>
      </div>

      {/* ดาวน์โหลดรายงานเวลาเข้า-ออกรายเดือน (CSV) */}
      <div className="card p-3 mb-3">
        <h3 className="font-medium text-sm mb-2">ดาวน์โหลดรายงานเวลาเข้า-ออกรายเดือน</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="label">เดือน</label>
            <input
              type="month"
              value={exportMonth}
              onChange={(e) => setExportMonth(e.target.value)}
              max={currentBkkMonth()}
              className="input text-sm"
            />
          </div>
          {branches && branches.length > 0 && (
            <div>
              <label className="label">สาขา</label>
              <select
                value={exportBranch}
                onChange={(e) => setExportBranch(e.target.value)}
                className="input text-sm"
              >
                <option value="">ทุกสาขา</option>
                {branches.filter((b) => b.is_active).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={handleExportAttendance}
            disabled={exporting || !exportMonth}
            className="btn-secondary text-sm"
          >
            {exporting ? 'กำลังสร้างไฟล์…' : 'ดาวน์โหลดรายงาน (CSV)'}
          </button>
        </div>
      </div>

      {/* by-shift breakdown */}
      {data && data.by_shift.length > 0 && (
        <div className="card p-3 mb-3">
          <h3 className="font-medium text-sm mb-2">แยกตามกะ</h3>
          <div className="space-y-1">
            {data.by_shift.map((s) => (
              <div key={s.shift_code} className="flex justify-between text-sm">
                <span>{s.shift_label} ({s.start_time}-{s.end_time})</span>
                <span className="text-gray-600">
                  <span className="text-emerald-700 font-medium">{s.scanned}</span>/{s.in_shift}
                  {s.on_leave > 0 && <span className="text-blue-600"> · ลา {s.on_leave}</span>}
                  {s.unscanned > 0 && <span className="text-amber-700"> · ค้าง {s.unscanned}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter by department / branch */}
      {((departments && departments.length > 0) || (branches && branches.length > 0)) && (
        <div className="grid grid-cols-2 gap-2 mb-3 max-w-md">
          {departments && departments.length > 0 && (
            <select
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="input text-sm"
            >
              <option value="">ทุกแผนก</option>
              {departments.map((d) => (
                <option key={d.id} value={d.name}>
                  {d.name}
                </option>
              ))}
            </select>
          )}
          {branches && branches.length > 0 && (
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="input text-sm"
            >
              <option value="">ทุกสาขา</option>
              {branches.filter((b) => b.is_active).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-3 overflow-x-auto">
        {([
          { id: 'unscanned', label: `ยังไม่สแกน (${data?.summary.unscanned ?? 0})` },
          { id: 'scanned', label: `สแกนแล้ว (${data?.summary.scanned_morning_in ?? 0})` },
          { id: 'on_leave', label: `ลา (${data?.summary.on_leave ?? 0})` },
        ] as const).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${
              tab === t.id ? 'bg-brand-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>}

      {tab === 'unscanned' && data && (
        <>
          {/* P14 — "ทวงไม่ได้" ต้องเห็นตั้งแต่บนสุด ไม่ใช่ซ่อนอยู่ในแถวใครแถวมัน:
              ถ้ามีคนที่ระบบเตือนไม่ถึง HR ต้องรู้ว่าต้องไปตามเองกี่คน */}
          {unscannedList.some((r) => r.nudge_blocked_reason) && (
            <div className="card p-3 mb-3 border-l-4 border-red-500">
              {unscannedList.filter((r) => r.nudge_blocked_reason === 'no_account').length > 0 && (
                <p className="text-sm font-medium text-red-700">
                  ใช้ระบบไม่ได้เลย {unscannedList.filter((r) => r.nudge_blocked_reason === 'no_account').length} คน
                  <span className="block text-xs font-normal text-gray-600 mt-0.5">
                    ยังไม่มีบัญชีเข้าระบบ — สแกนเข้างานไม่ได้ตั้งแต่แรก จะขึ้นค้างในรายการนี้ทุกวัน
                    ต้องเปิดบัญชีให้ก่อน (เมนู “พนักงานทั้งหมด” → เปิดโปรไฟล์ → ผูกไลน์ หรือออกรหัสเข้าเว็บ)
                  </span>
                </p>
              )}
              {unscannedList.filter((r) => r.nudge_blocked_reason === 'no_line').length > 0 && (
                <p className="text-sm font-medium text-red-700 mt-2">
                  เตือนทางไลน์ไม่ถึง {unscannedList.filter((r) => r.nudge_blocked_reason === 'no_line').length} คน
                  <span className="block text-xs font-normal text-gray-600 mt-0.5">
                    กลุ่มนี้เข้าหน้าเว็บสแกนได้ แต่ไม่ได้ผูกไลน์ กดเตือนแล้วไม่ถึงตัว ต้องโทรหรือบอกปากเปล่า
                  </span>
                </p>
              )}
            </div>
          )}
          {unscannedList.filter((r) => r.can_nudge_now).length > 1 && (
            <button
              onClick={() =>
                handleNudge(unscannedList.filter((r) => r.can_nudge_now).map((r) => r.emp_code))
              }
              disabled={nudge.isPending}
              className="btn-primary w-full mb-3 text-sm"
            >
              📨 แจ้งเตือนทุกคนที่ยังไม่สแกน ({unscannedList.filter((r) => r.can_nudge_now).length})
            </button>
          )}
          {unscannedList.length === 0 ? (
            <div className="card p-6 text-center text-gray-400">
              <p className="text-4xl mb-2">🎉</p>
              <p className="text-sm">ทุกคนสแกนเข้างานครบแล้ว</p>
            </div>
          ) : (
            <div className="space-y-2">
              {unscannedList.map((row) => (
                <UnscannedCard key={row.emp_code} row={row} branch={branchNameByEmp(row.emp_code)} onNudge={() => handleNudge([row.emp_code])} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'scanned' && data && (
        <div className="space-y-2">
          {scannedList.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">ยังไม่มีใครสแกนวันนี้</p>
          ) : (
            scannedList.map((row) => (
              <ScannedCard
                key={row.emp_code}
                row={row}
                branch={branchNameByEmp(row.emp_code)}
                // แสดง badge เฉพาะตอนเช็คอินคนละสาขากับสาขาหลัก (name-map มีสาขาปิดด้วย)
                checkinBranch={
                  row.checkin_branch_id && row.checkin_branch_id !== (empBranch.get(row.emp_code) || null)
                    ? branchNameById.get(row.checkin_branch_id) ?? null
                    : null
                }
              />
            ))
          )}
        </div>
      )}

      {tab === 'on_leave' && data && (
        <div className="space-y-2">
          {onLeaveList.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">ไม่มีคนลาวันนี้</p>
          ) : (
            onLeaveList.map((row) => <OnLeaveCard key={row.emp_code} row={row} branch={branchNameByEmp(row.emp_code)} />)
          )}
        </div>
      )}
    </>
  );
}

/** Standalone page kept for legacy /hr/attendance-today (now redirects via Time hub). */
export function HRAttendanceToday() {
  return (
    <AdminLayout title="การเข้างานวันนี้">
      <HRAttendanceTodayContent />
    </AdminLayout>
  );
}

function UnscannedCard({ row, branch, onNudge }: { row: UnscannedRow; branch?: string | null; onNudge: () => void }) {
  return (
    <div className="card p-3 flex items-center justify-between">
      <div className="min-w-0">
        <p className="font-medium text-sm truncate">
          {row.first_name} {row.last_name}
          {row.nickname && <span className="text-gray-500"> ({row.nickname})</span>}
        </p>
        <p className="text-xs text-gray-500">
          {row.department ?? '-'} · {row.shift_code ?? '-'} ({row.shift_start ?? '-'}){branch ? ` · ${branch}` : ''}
        </p>
        {row.minutes_late > 0 && <p className="text-xs text-amber-700">เลย {row.minutes_late} นาที</p>}
        {row.last_nudged_at && (
          <p className="text-xs text-gray-400">เตือนล่าสุด {formatRelative(row.last_nudged_at)}</p>
        )}
        {row.nudge_blocked_reason === 'no_line' && (
          <p className="text-xs text-red-700">ยังไม่ผูกไลน์ — เข้าหน้าเว็บสแกนได้ แต่ระบบเตือนไม่ถึง ต้องโทรหาเอง</p>
        )}
        {row.nudge_blocked_reason === 'no_account' && (
          <p className="text-xs text-red-700">ยังไม่มีบัญชีเข้าระบบ — สแกนเข้างานไม่ได้เลย จะขึ้นค้างตรงนี้ทุกวันจนกว่าจะเปิดบัญชีให้</p>
        )}
      </div>
      <button
        disabled={!row.can_nudge_now}
        onClick={onNudge}
        className={`text-xs px-3 py-1.5 rounded-lg whitespace-nowrap ml-2 shrink-0 ${
          row.can_nudge_now ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-500'
        }`}
      >
        {row.can_nudge_now ? '📨 เตือน' : row.nudge_blocked_reason === 'no_line' ? '✖ ทวงไม่ได้' : '⏳ รอ 15 น.'}
      </button>
    </div>
  );
}

function ScannedCard({ row, branch, checkinBranch }: { row: ScannedRow; branch?: string | null; checkinBranch?: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const t = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Bangkok',
  }).format(new Date(row.morning_in_at));
  const hasPic = !!row.selfie_r2_path;
  return (
    <div className="card p-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => hasPic && setExpanded((v) => !v)}
          disabled={!hasPic}
          className="text-left flex-1 min-w-0"
        >
          <p className="font-medium text-sm">
            {row.first_name} {row.last_name}
            {row.nickname && <span className="text-gray-500"> ({row.nickname})</span>}
            {hasPic && <span className="text-gray-400 ml-1 text-xs">📷</span>}
          </p>
          <p className="text-xs text-gray-500">{row.department ?? '-'}{branch ? ` · ${branch}` : ''}</p>
          {checkinBranch && (
            <span className="inline-flex items-center gap-1 text-[10px] text-sky-700 bg-sky-50 rounded-full px-2 py-0.5 mt-0.5">
              <MapPin className="w-3 h-3" /> เช็คอินที่ {checkinBranch}
            </span>
          )}
          {row.approval_status === 'pending_review' && (
            <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> รอ HR ตรวจสอบ</p>
          )}
          {!row.geofence_passed && <p className="text-xs text-amber-700 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> นอกพื้นที่</p>}
        </button>
        <div className="text-right shrink-0 ml-2">
          <p className="font-mono text-base font-bold text-emerald-700">{t}</p>
          {row.minutes_late > 0 && <p className="text-xs text-amber-700">+{row.minutes_late} น.</p>}
        </div>
      </div>
      {expanded && hasPic && (
        <div className="mt-2">
          <img
            src={resolveFileUrl(row.selfie_r2_path) ?? ''}
            alt={`selfie ${row.emp_code}`}
            className="w-full max-w-xs rounded-lg border border-gray-200"
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
}

function OnLeaveCard({ row, branch }: { row: OnLeaveRow; branch?: string | null }) {
  return (
    <div className="card p-3">
      <p className="font-medium text-sm">
        {row.first_name} {row.last_name}
        {row.nickname && <span className="text-gray-500"> ({row.nickname})</span>}
      </p>
      <p className="text-xs text-gray-500">{row.department ?? '-'}{branch ? ` · ${branch}` : ''}</p>
      <p className="text-xs text-blue-700 mt-1">
        ลา {row.leave_type}
        {row.reason ? ` — ${row.reason}` : ''}
      </p>
    </div>
  );
}
